import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WeddingProvider } from "@/context/WeddingContext";
import { BachProvider, useBach } from "@/context/BachContext";
import AppLayout from "@/components/AppLayout";
import BachLayout from "@/components/BachLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Budget from "@/pages/Budget";
import Guests from "@/pages/Guests";
import Timeline from "@/pages/Timeline";
import Vendors from "@/pages/Vendors";
import CalendarPage from "@/pages/CalendarPage";
import Seating from "@/pages/Seating";
import Setup from "@/pages/Setup";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import Admin from "@/pages/Admin";
import Paywall from "@/pages/Paywall";
import AuthCallback from "@/pages/AuthCallback";
import Claim from "@/pages/Claim";
import Planners from "@/pages/Planners";
import OurPlanners from "@/pages/OurPlanners";
import BachDashboard from "@/pages/bach/BachDashboard";
import BachSetup from "@/pages/bach/BachSetup";
import BachSection from "@/pages/bach/BachSection";
import BachTheme from "@/pages/bach/BachTheme";
import BachNotes from "@/pages/bach/BachNotes";
import { DemoBoot, DemoChrome } from "@/components/Demo";
import {
  Party, Venues, Food, Itinerary, Packing, Theme, Gallery, Music, Gifts, Honeymoon, Contacts,
} from "@/pages/Sections";

function FullLoader() {
  return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin" /></div>;
}

function Protected({ children }) {
  const { authed } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  return children;
}

function RequireWedding({ children }) {
  const { authed, hasWedding, hasAccess } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  if (!hasWedding) return <Navigate to={hasAccess ? "/onboarding" : "/demo/wedding"} replace />;
  return <WeddingProvider><AppLayout>{children}</AppLayout></WeddingProvider>;
}

function RequireOnboarding({ children }) {
  const { authed, hasWedding, hasAccess } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  if (hasWedding) return <Navigate to="/app" replace />;
  if (!hasAccess) return <Navigate to="/paywall" replace />;
  return children;
}

function PaywallGate({ children }) {
  const { authed, hasWedding, hasAccess } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  if (hasWedding) return <Navigate to="/app" replace />;
  if (hasAccess) return <Navigate to="/onboarding" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { authed, user } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  return <WeddingProvider><AppLayout>{children}</AppLayout></WeddingProvider>;
}

// ---- Bachelorette guards (fully separate product) ----
function RequireBachAccess({ children }) {
  const { authed, user } = useAuth();
  if (authed === null) return <FullLoader />;
  if (!authed) return <Navigate to="/login" replace />;
  if (!user?.products?.bachelorette_planner) return <Navigate to="/demo/bachelorette" replace />;
  return children;
}

function BachGate({ children }) {
  const { profile, loading } = useBach();
  if (loading) return <FullLoader />;
  if (!profile) return <Navigate to="/bach/onboarding" replace />;
  return <BachLayout>{children}</BachLayout>;
}

function BachApp({ children }) {
  return (
    <RequireBachAccess>
      <BachProvider><BachGate>{children}</BachGate></BachProvider>
    </RequireBachAccess>
  );
}

// ---- Public read-only DEMO shells (no login; reuse the REAL interfaces) ----
function DemoWedding() {
  return (
    <DemoBoot product="wedding_planner">
      <WeddingProvider>
        <AppLayout base="/demo/wedding" demo>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="setup" element={<Setup />} />
            <Route path="budget" element={<Budget />} />
            <Route path="guests" element={<Guests />} />
            <Route path="timeline" element={<Timeline />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="seating" element={<Seating />} />
            <Route path="party" element={<Party />} />
            <Route path="venues" element={<Venues />} />
            <Route path="food" element={<Food />} />
            <Route path="itinerary" element={<Itinerary />} />
            <Route path="packing" element={<Packing />} />
            <Route path="theme" element={<Theme />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="music" element={<Music />} />
            <Route path="gifts" element={<Gifts />} />
            <Route path="honeymoon" element={<Honeymoon />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="settings" element={<Settings />} />
            <Route path="help" element={<Help />} />
            <Route path="*" element={<Navigate to="/demo/wedding" replace />} />
          </Routes>
        </AppLayout>
      </WeddingProvider>
      <DemoChrome product="wedding_planner" />
    </DemoBoot>
  );
}

function DemoBachelorette() {
  return (
    <DemoBoot product="bachelorette_planner">
      <BachProvider>
        <BachLayout base="/demo/bachelorette" demo>
          <Routes>
            <Route index element={<BachDashboard />} />
            <Route path="setup" element={<BachSetup />} />
            <Route path="theme" element={<BachTheme />} />
            <Route path="notes" element={<BachNotes />} />
            <Route path=":section" element={<BachSection />} />
            <Route path="*" element={<Navigate to="/demo/bachelorette" replace />} />
          </Routes>
        </BachLayout>
      </BachProvider>
      <DemoChrome product="bachelorette_planner" />
    </DemoBoot>
  );
}

function AppShell() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/our-planners" element={<OurPlanners />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/claim" element={<Claim />} />
      <Route path="/planners" element={<Protected><Planners /></Protected>} />
      <Route path="/onboarding" element={<RequireOnboarding><Onboarding /></RequireOnboarding>} />
      <Route path="/paywall" element={<PaywallGate><Paywall /></PaywallGate>} />

      {/* Wedding Planner (existing — untouched) */}
      <Route path="/app" element={<RequireWedding><Dashboard /></RequireWedding>} />
      <Route path="/app/setup" element={<RequireWedding><Setup /></RequireWedding>} />
      <Route path="/app/budget" element={<RequireWedding><Budget /></RequireWedding>} />
      <Route path="/app/guests" element={<RequireWedding><Guests /></RequireWedding>} />
      <Route path="/app/timeline" element={<RequireWedding><Timeline /></RequireWedding>} />
      <Route path="/app/vendors" element={<RequireWedding><Vendors /></RequireWedding>} />
      <Route path="/app/calendar" element={<RequireWedding><CalendarPage /></RequireWedding>} />
      <Route path="/app/seating" element={<RequireWedding><Seating /></RequireWedding>} />
      <Route path="/app/party" element={<RequireWedding><Party /></RequireWedding>} />
      <Route path="/app/venues" element={<RequireWedding><Venues /></RequireWedding>} />
      <Route path="/app/food" element={<RequireWedding><Food /></RequireWedding>} />
      <Route path="/app/itinerary" element={<RequireWedding><Itinerary /></RequireWedding>} />
      <Route path="/app/packing" element={<RequireWedding><Packing /></RequireWedding>} />
      <Route path="/app/theme" element={<RequireWedding><Theme /></RequireWedding>} />
      <Route path="/app/gallery" element={<RequireWedding><Gallery /></RequireWedding>} />
      <Route path="/app/music" element={<RequireWedding><Music /></RequireWedding>} />
      <Route path="/app/gifts" element={<RequireWedding><Gifts /></RequireWedding>} />
      <Route path="/app/honeymoon" element={<RequireWedding><Honeymoon /></RequireWedding>} />
      <Route path="/app/contacts" element={<RequireWedding><Contacts /></RequireWedding>} />
      <Route path="/app/settings" element={<RequireWedding><Settings /></RequireWedding>} />
      <Route path="/app/help" element={<RequireWedding><Help /></RequireWedding>} />
      <Route path="/app/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />

      {/* Bachelorette Planner (new — separate product & data) */}
      <Route path="/bach/onboarding" element={<RequireBachAccess><BachProvider><BachSetup onboarding /></BachProvider></RequireBachAccess>} />
      <Route path="/bach" element={<BachApp><BachDashboard /></BachApp>} />
      <Route path="/bach/setup" element={<BachApp><BachSetup /></BachApp>} />
      <Route path="/bach/theme" element={<BachApp><BachTheme /></BachApp>} />
      <Route path="/bach/notes" element={<BachApp><BachNotes /></BachApp>} />
      <Route path="/bach/:section" element={<BachApp><BachSection /></BachApp>} />

      {/* Public read-only demos */}
      <Route path="/demo/wedding/*" element={<DemoWedding />} />
      <Route path="/demo/bachelorette/*" element={<DemoBachelorette />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
